import React, { useState, useCallback, useMemo } from 'react';
import { Product, Stream } from '@/types/liveStreamTypes';
import LiveStreamPurchase from './LiveStreamPurchase';
import { ChevronLeft, ChevronRight, Minus, Plus, Search, Grid, List, Share2, ShoppingBag } from 'lucide-react';

interface LiveStreamProductsProps {
  products: Product[];
  stream: Stream;
  user: {
    $id: string;
    name: string;
    email?: string;
    phone?: string;
  };
  onPurchase: (data: {
    productId: string;
    orderId: string;
    paymentId: string;
    selectedImage?: string;
    quantity?: number;
  }) => void;
  featuredProductId?: string;
  isCreator: boolean;
  onFeatureProduct?: (product: Product) => void;
}

interface ProductCardProps {
  product: Product;
  stream: Stream;
  user: LiveStreamProductsProps['user'];
  onPurchase: LiveStreamProductsProps['onPurchase'];
  isFeatured?: boolean;
  isCreator: boolean;
  onFeatureProduct?: (product: Product) => void;
  viewMode?: 'grid' | 'list';
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  stream,
  user,
  onPurchase,
  isFeatured = false,
  isCreator,
  onFeatureProduct,
  viewMode = 'grid'
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(product.minQuantity ? parseInt(product.minQuantity) : 1);
  
  const handleImageChange = useCallback((direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setSelectedImageIndex(prev => 
        prev === 0 ? product.images.length - 1 : prev - 1
      );
    } else {
      setSelectedImageIndex(prev => 
        prev === product.images.length - 1 ? 0 : prev + 1
      );
    }
  }, [product.images.length]);

  const handleQuantityChange = useCallback((action: 'increase' | 'decrease') => {
    setQuantity((prev: number) => {
      const minQty = product.minQuantity ? parseInt(product.minQuantity) : 1;
      const maxQty = product.maxQuanity ? parseInt(product.maxQuanity) : 99;
      
      if (action === 'increase') {
        return Math.min(prev + 1, maxQty);
      } else {
        return Math.max(prev - 1, minQty);
      }
    });
  }, [product.minQuantity, product.maxQuanity]);


  const discountPercentage = useMemo(() => 
    product.discount ? parseInt(product.discount) : 0,
    [product.discount]
  );

  const originalPrice = useMemo(() => {
    if (discountPercentage > 0) {
      const original = parseFloat(product.price) / (1 - discountPercentage / 100);
      return (original * quantity).toFixed(2);
    }
    return null;
  }, [product.price, discountPercentage, quantity]);

  const handlePurchaseSuccess = useCallback((purchaseData: any) => {
    onPurchase({
      ...purchaseData,
      selectedImage: product.images[selectedImageIndex],
      quantity
    });
  }, [onPurchase, product.images, selectedImageIndex, quantity]);

  const handleShare = useCallback(() => {
    if (navigator.share) {
      navigator.share({
        title: product.title,
        text: `Check out this product: ${product.title}`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  }, [product.title]);

  // Calculate quantity constraints
  const minQty = product.minQuantity ? parseInt(product.minQuantity) : 1;
  const maxQty = product.maxQuanity ? parseInt(product.maxQuanity) : 99;
  const canDecrease = quantity > minQty;
  const canIncrease = quantity < maxQty;

  // List view for mobile
  if (viewMode === 'list') {
    return (
      <div className={`bg-white rounded-lg text-gray-700 shadow-sm overflow-hidden transition-all hover:shadow-md border ${
        isFeatured ? 'border-orange-400' : 'border-gray-200'
      }`}>
        <div className="flex">
          {/* Image Section - Fixed responsive sizing */}
          <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0">
            <img 
              src={product.images[selectedImageIndex] || product.images[0]} 
              alt={product.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/assets/images/placeholder-product.png';
              }}
            />
            
            {/* Badges */}
            {isFeatured && (
              <div className="absolute top-0.5 left-0.5 bg-orange-500 text-white px-1 py-0.5 text-xs font-bold rounded">
                FEATURED
              </div>
            )}
            {discountPercentage > 0 && (
              <div className="absolute top-0.5 right-0.5 bg-rose-500 text-white px-1 py-0.5 text-xs font-bold rounded">
                {discountPercentage}% OFF
              </div>
            )}

            {/* Image Navigation for multiple images */}
            {product.images.length > 1 && (
              <>
                <button
                  onClick={() => handleImageChange('prev')}
                  className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-black/50 text-white rounded-full p-0.5"
                >
                  <ChevronLeft size={8} />
                </button>
                <button
                  onClick={() => handleImageChange('next')}
                  className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-black/50 text-white rounded-full p-0.5"
                >
                  <ChevronRight size={8} />
                </button>
              </>
            )}
          </div>

          {/* Content Section */}
          <div className="flex-1 p-3 min-w-0">
            <div className="h-full flex flex-col">
              <div className="flex-1">
                <h3 className="font-semibold text-sm text-gray-900 mb-1 line-clamp-2">
                  {product.title}
                </h3>

                {/* Price - FIXED: Show totalPrice instead of product.price */}
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-lg font-bold text-gray-900">₹{product.price}</span>
                  {originalPrice && (
                    <span className="text-xs text-gray-500 line-through">₹{originalPrice}</span>
                  )}
                </div>

                {/* Quantity Selector - Compact for list view */}
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-xs text-gray-600">Qty:</span>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleQuantityChange('decrease')}
                      disabled={!canDecrease}
                      className="w-6 h-6 rounded-full bg-gray-100 border border-gray-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Minus size={10} />
                    </button>
                    <span className="font-medium text-sm w-6 text-center">{quantity}</span>
                    <button
                      onClick={() => handleQuantityChange('increase')}
                      disabled={!canIncrease}
                      className="w-6 h-6 rounded-full bg-gray-100 border border-gray-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus size={10} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Actions */}
              {!isCreator && product.status !== 'sold' && (
                <div className="mt-2">
                  <LiveStreamPurchase 
                    stream={stream}
                    product={{
                      ...product,
                      price: product.price, // Unit price only
                      selectedImage: product.images[selectedImageIndex],
                      quantity: quantity // Pass quantity separately
                    }}
                    user={user}
                    onSuccess={handlePurchaseSuccess}
                  />
                </div>
              )}

              {isCreator && (
                <button
                  onClick={() => onFeatureProduct?.(product)}
                  disabled={isFeatured || !onFeatureProduct}
                  className="mt-2 w-full rounded bg-blue-50 py-1 text-xs text-blue-600 disabled:cursor-default disabled:opacity-100"
                >
                  {isFeatured ? "Your Product" : "Add to live"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Grid view (default) - Fixed responsive sizing
  return (
    <div className={`bg-white rounded-lg shadow-sm text-gray-800 overflow-hidden transition-all hover:shadow-md group ${
      isFeatured ? 'ring-2 ring-orange-400' : ''
    }`}>
      {/* Image Section - Responsive aspect ratio */}
      <div className="relative w-full aspect-square bg-gray-100">
        <img 
          src={product.images[selectedImageIndex] || product.images[0]} 
          alt={product.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/assets/images/placeholder-product.png';
          }}
        />
        
        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-col space-y-1">
          {isFeatured && (
            <span className="bg-orange-500 text-white px-2 py-1 text-xs font-bold rounded">
              FEATURED
            </span>
          )}
          {discountPercentage > 0 && (
            <span className="bg-rose-500 text-white px-2 py-1 text-xs font-bold rounded">
              {discountPercentage}% OFF
            </span>
          )}
        </div>

        {/* Image Navigation */}
        {product.images.length > 1 && (
          <>
            <button
              onClick={() => handleImageChange('prev')}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => handleImageChange('next')}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronRight size={14} />
            </button>
            
            {/* Image Indicators */}
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
              {product.images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImageIndex(index)}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    index === selectedImageIndex ? 'bg-white' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {/* Sold Out Overlay */}
        {product.status === 'sold' && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <span className="text-white font-bold">SOLD OUT</span>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-4">
        <div className="mb-3">
          <h3 className="font-semibold text-lg text-gray-900 mb-1 line-clamp-2">
            {product.title}
          </h3>
          <p className="text-gray-600 text-sm line-clamp-2">
            {product.description}
          </p>
        </div>

        {/* Price Section */}
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-1">
            <span className="text-xl font-bold text-gray-900">₹{product.price}</span>
            {originalPrice && (
              <span className="text-sm text-gray-500 line-through">₹{originalPrice}</span>
            )}
          </div>
        </div>

        {/* Image Thumbnails - Fixed sizing */}
        {product.images.length > 1 && (
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Designs ({product.images.length}):</p>
            <div className="flex space-x-2 overflow-x-auto pb-2">
              {product.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImageIndex(index)}
                  className={`flex-shrink-0 w-12 h-12 rounded border-2 overflow-hidden ${
                    index === selectedImageIndex 
                      ? 'border-blue-500' 
                      : 'border-gray-200'
                  }`}
                >
                  <img
                    src={image}
                    alt={`Design ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/assets/images/placeholder-product.png';
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quantity Selector - Fixed logic */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Quantity:</span>
            <div className="text-xs text-gray-500">
              {product.minQuantity && product.maxQuanity ? (
                <span>Min: {product.minQuantity} | Max: {product.maxQuanity}</span>
              ) : product.minQuantity ? (
                <span>Min: {product.minQuantity}</span>
              ) : product.maxQuanity ? (
                <span>Max: {product.maxQuanity}</span>
              ) : null}
            </div>
          </div>
          <div className="flex items-center justify-center space-x-3 bg-gray-50 rounded-lg p-2">
            <button
              onClick={() => handleQuantityChange('decrease')}
              disabled={!canDecrease}
              className="w-8 h-8 rounded-full bg-white border border-gray-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              <Minus size={14} />
            </button>
            <span className="font-bold text-lg w-8 text-center">{quantity}</span>
            <button
              onClick={() => handleQuantityChange('increase')}
              disabled={!canIncrease}
              className="w-8 h-8 rounded-full bg-white border border-gray-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              <Plus size={14} />
            </button>
          </div>
          {!canIncrease && quantity >= maxQty && (
            <p className="text-xs text-orange-600 mt-1 text-center">Maximum quantity reached</p>
          )}
          {!canDecrease && quantity <= minQty && (
            <p className="text-xs text-blue-600 mt-1 text-center">Minimum quantity required</p>
          )}
        </div>

        {/* Action Buttons */}
        {product.status !== 'sold' && !isCreator && (
          <div className="space-y-3">
            <LiveStreamPurchase 
                  stream={stream}
                  product={{
                    ...product,
                    price: product.price, // Unit price
                    selectedImage: product.images[selectedImageIndex],
                    quantity: quantity
                  }}
                  user={user}
                  onSuccess={handlePurchaseSuccess}
              />
            
            <div className="grid grid-cols-1 gap-2">
              <button 
                onClick={handleShare}
                className="flex items-center justify-center space-x-1 py-2 px-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Share2 size={14} />
                <span className="text-sm">Share</span>
              </button>
            </div>
          </div>
        )}

        {isCreator && (
          <button
            onClick={() => onFeatureProduct?.(product)}
            disabled={isFeatured || !onFeatureProduct}
            className="w-full rounded-lg bg-blue-50 py-3 text-sm font-medium text-blue-700 disabled:cursor-default disabled:opacity-100"
          >
            {isFeatured ? "Your Product" : "Add to live"}
          </button>
        )}
      </div>
    </div>
  );
};

const LiveStreamProducts: React.FC<LiveStreamProductsProps> = ({
  products,
  stream,
  user,
  onPurchase,
  isCreator,
  onFeatureProduct,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const activeProducts = useMemo(() => 
    products.filter(product => product.isActivated),
    [products]
  );

  const featuredProducts = useMemo(() => 
    stream?.featuredProducts ? activeProducts.filter(p => stream.featuredProducts.includes(p.$id)) : [],
    [activeProducts, stream?.featuredProducts]
  );

  // Filter products based on search query
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) {
      return {
        featured: featuredProducts,
        others: activeProducts.filter(p => !stream?.featuredProducts?.includes(p.$id)),
        all: activeProducts
      };
    }
    
    // When searching, filter all products
    const searchResults = activeProducts.filter(product => 
      product.title.toLowerCase().includes(searchQuery.toLowerCase().trim()) || 
      product.description.toLowerCase().includes(searchQuery.toLowerCase().trim())
    );
    
    return {
      featured: searchResults.filter(p => stream?.featuredProducts?.includes(p.$id)),
      others: searchResults.filter(p => !stream?.featuredProducts?.includes(p.$id)),
      all: searchResults
    };
  }, [activeProducts, searchQuery, featuredProducts, stream?.featuredProducts]);

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  if (activeProducts.length === 0) {
    return (
      <div className="bg-gray-50 p-6 text-center">
        <ShoppingBag size={48} className="text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-800 mb-2">No Products Available</h3>
        <p className="text-gray-600">The seller hasn't added any products yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-full mb-10">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Products</h2>
            <p className="text-sm text-gray-600">
              {searchQuery ? `${filteredProducts.all.length} results` : `${activeProducts.length} items`}
            </p>
          </div>
          
          {/* View Toggle */}
          <div className="flex items-center text-gray-800 space-x-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
            >
              <Grid size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
            >
              <List size={16} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={handleSearch}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 text-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {filteredProducts.all.length === 0 ? (
          <div className="text-center py-8">
            <Search size={32} className="text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No products found</h3>
            <p className="text-gray-600 mb-4">Try searching with different keywords</p>
            <button
              onClick={() => setSearchQuery('')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Clear search
            </button>
          </div>
        ) : (
          <>
            {/* Featured Products - only show if not searching and there are featured products */}
            {!searchQuery && filteredProducts.featured.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-bold mb-3 text-gray-900">Featured Products</h3>
                <div className={`${
                  viewMode === 'grid' 
                    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4' 
                    : 'space-y-3'
                }`}>
                  {filteredProducts.featured.map(product => (
                    <ProductCard
                      key={product.$id}
                      product={product}
                      stream={stream}
                      user={user}
                      onPurchase={onPurchase}
                      isFeatured={true}
                      isCreator={isCreator}
                      onFeatureProduct={onFeatureProduct}
                      viewMode={viewMode}
                    />
                  ))}
                </div>
              </div>
            )}

            {isCreator && !searchQuery && filteredProducts.others.length > 0 && (
              <div>
                <h3 className="text-lg font-bold mb-3 text-gray-900">Available Products</h3>
                <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-3'}>
                  {filteredProducts.others.map(product => (
                    <ProductCard
                      key={product.$id}
                      product={product}
                      stream={stream}
                      user={user}
                      onPurchase={onPurchase}
                      isCreator={true}
                      onFeatureProduct={onFeatureProduct}
                      viewMode={viewMode}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Search Results only when searching */}
            {searchQuery && (
              <div>
                <h3 className="text-lg font-bold mb-3 text-gray-900">
                  Search Results ({filteredProducts.all.length})
                </h3>
                
                <div className={`${
                  viewMode === 'grid'
                    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'
                    : 'space-y-3'
                }`}>
                  {filteredProducts.all.map((product) => (
                    <ProductCard
                      key={product.$id}
                      product={product}
                      stream={stream}
                      user={user}
                      onPurchase={onPurchase}
                      isFeatured={stream?.featuredProducts?.includes(product.$id)}
                      isCreator={isCreator}
                      onFeatureProduct={onFeatureProduct}
                      viewMode={viewMode}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LiveStreamProducts;
